import { useEffect, useState } from "react";
import SmartMedia from "@/components/feature/SmartMedia";
import { isVideoUrl } from "@/lib/media";
import {
  formatPrice,
  getProductImages,
  productCover,
  timeAgo,
  type AdminProduct,
} from "@/pages/admin/dashboard/types";

interface ProductPreviewModalProps {
  product: AdminProduct | null;
  sellerName: string;
  busy: boolean;
  onApprove: (product: AdminProduct) => void;
  onReject: (product: AdminProduct) => void;
  onClose: () => void;
}

export default function ProductPreviewModal({
  product,
  sellerName,
  busy,
  onApprove,
  onReject,
  onClose,
}: ProductPreviewModalProps) {
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    if (product) setActiveImage(0);
  }, [product?.id]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (product) {
      document.addEventListener("keydown", onKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [product, onClose]);

  if (!product) return null;

  const images = getProductImages(product);
  const activeMedia = images[activeImage] ?? productCover(product);
  const isNew = (product.condition ?? "").toLowerCase() === "nuevo";

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground-950/50 p-4 backdrop-blur-sm sm:p-6"
      onClick={onClose}
    >
      <div
        className="my-auto w-full max-w-3xl overflow-hidden rounded-lg border border-background-200 bg-background-50"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Vista previa de ${product.title}`}
      >
        {/* Encabezado */}
        <div className="flex items-center justify-between gap-3 border-b border-background-200/70 px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-eye-line text-base"></i>
            </span>
            <div>
              <h2 className="font-heading text-base font-bold text-foreground-950">
                Vista previa de la publicación
              </h2>
              <p className="text-xs text-foreground-500">
                Estado: pendiente de moderación
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
            aria-label="Cerrar"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Contenido */}
        <div className="grid grid-cols-1 gap-6 p-5 sm:p-6 md:grid-cols-2">
          {/* Galería */}
          <div>
            <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
              {activeMedia ? (
                <SmartMedia
                  src={activeMedia}
                  alt={product.title}
                  className="h-full w-full object-cover object-top"
                />
              ) : (
                <span className="flex h-full w-full flex-col items-center justify-center gap-2 text-foreground-300">
                  <i className="ri-image-line text-5xl"></i>
                  <span className="text-xs font-medium text-foreground-400">
                    Este producto no tiene imágenes
                  </span>
                </span>
              )}
              <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
                <span
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
                    isNew
                      ? "bg-secondary-500 text-white"
                      : "bg-background-800 text-white dark:bg-background-200 dark:text-foreground-950"
                  }`}
                >
                  {product.condition === "nuevo" ? "Nuevo" : "Usado"}
                </span>
              </div>
            </div>

            {images.length > 1 && (
              <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
                {images.map((image, index) => {
                  const video = isVideoUrl(image);
                  return (
                    <button
                      key={image}
                      type="button"
                      onClick={() => setActiveImage(index)}
                      aria-label={`Ver ${video ? "video" : "imagen"} ${index + 1}`}
                      className={`relative h-16 w-16 shrink-0 cursor-pointer overflow-hidden rounded-md border-2 transition-colors ${
                        activeImage === index
                          ? "border-primary-400"
                          : "border-background-200 hover:border-background-300"
                      }`}
                    >
                      {video ? (
                        <span className="flex h-full w-full items-center justify-center bg-background-800 dark:bg-background-200">
                          <i className="ri-play-circle-fill text-xl text-white dark:text-foreground-950"></i>
                        </span>
                      ) : (
                        <SmartMedia
                          src={image}
                          alt={`${product.title} ${index + 1}`}
                          className="h-full w-full object-cover object-top"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Información */}
          <div className="flex flex-col">
            <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950">
              {product.title}
            </h3>

            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-heading text-2xl font-bold text-primary-600">
                {formatPrice(product.price, product.currency)}
              </span>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {timeAgo(product.created_at)}
              </span>
              {product.category && (
                <span className="flex items-center gap-1.5">
                  <i className="ri-price-tag-3-line"></i>
                  {product.category}
                </span>
              )}
            </div>

            {/* Vendedor */}
            <div className="mt-5 flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-100 p-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-base font-semibold text-white">
                {sellerName.charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground-950">
                  {sellerName}
                </p>
                <p className="text-xs text-foreground-500">Vendedor</p>
              </div>
            </div>

            {/* Descripción */}
            <div className="mt-5 flex-1">
              <h4 className="text-sm font-semibold text-foreground-950">
                Descripción
              </h4>
              <p className="mt-1.5 max-h-40 overflow-y-auto whitespace-pre-line text-sm leading-relaxed text-foreground-600">
                {product.description || "El vendedor no agregó una descripción."}
              </p>
            </div>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex flex-col-reverse gap-3 border-t border-background-200/70 bg-background-100/40 px-5 py-4 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={() => onReject(product)}
            disabled={busy}
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5 text-sm font-semibold text-accent-700 transition-colors hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className="ri-delete-bin-line"></i>
            Rechazar
          </button>
          <button
            type="button"
            onClick={() => onApprove(product)}
            disabled={busy}
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-check-line"></i>
            )}
            Aprobar publicación
          </button>
        </div>
      </div>
    </div>
  );
}